import { Box, Button, Divider, Flex, Input, Select, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useAuth } from "../../../Hooks/useAuth";
import { api } from "../../../services/api";
import { EditCategory } from "../EditCategory";
import { FiPlus, FiX } from "react-icons/fi";
import swal from "sweetalert";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

export function CategoryCard({ allCategories, allSubcategories, allProductTypes, allProducts }) {
  const { user } = useAuth();
  const [categoryName, setCategoryName] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [categories, setCategories] = useState(allCategories);
  const [subcategories] = useState(allSubcategories);
  const [showForm, setShowForm] = useState(false);

  // Related-data lookups for the "quantidade de produtos" / subcategoria pai
  // shown per row — built once from the dashboard's already-fetched data
  // instead of a dedicated endpoint per category.
  const subcategoryById = useMemo(
    () => Object.fromEntries((allSubcategories || []).map(s => [s.id, s])),
    [allSubcategories]
  );
  const productTypesByCategory = useMemo(() => {
    const map = {};
    (allProductTypes || []).forEach(pt => {
      (map[pt.category_id] ||= []).push(pt);
    });
    return map;
  }, [allProductTypes]);
  const productCountByType = useMemo(() => {
    const map = {};
    (allProducts || []).forEach(p => {
      map[p.product_type_id] = (map[p.product_type_id] || 0) + 1;
    });
    return map;
  }, [allProducts]);

  function relatedInfo(category) {
    const types = productTypesByCategory[category.id] || [];
    const productCount = types.reduce((sum, t) => sum + (productCountByType[t.id] || 0), 0);
    return {
      subcategory: subcategoryById[category.subcategory_id],
      typeCount: types.length,
      typeNames: types.map(t => t.name),
      productCount,
    };
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!categoryName.trim() || !subcategoryId) {
      swal({ title: "Atenção", text: "Nome e subcategoria não podem ficar em branco.", icon: "info" });
      return;
    }
    const response = await api.post("api/v1/categories", { name: categoryName, user_id: user.id, subcategory_id: subcategoryId }, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json" },
    });
    if (response.status === 200) {
      setCategories(prev => [...prev, response.data]);
      setCategoryName("");
      setSubcategoryId("");
      setShowForm(false);
      swal({ title: "Criado!", text: "Categoria criada com sucesso.", icon: "success" });
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
    }
  }

  const updateCategoryName = (categoryId, newName) => {
    setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, name: newName } : c));
  };

  const inputStyle = {
    borderRadius: "8px",
    fontSize: "14px",
    border: "1px solid rgba(0,74,51,.2)",
    _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
  };

  return (
    <Box>
      {/* Section header */}
      <Flex align="center" justify="space-between" mb="20px">
        <Box>
          <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
            Catálogo
          </Text>
          <Text fontSize="20px" fontWeight="900" color={GREEN}>
            Categorias
            <Text as="span" fontSize="14px" fontWeight="600" color="gray.400" ml="8px">
              ({categories.length})
            </Text>
          </Text>
        </Box>
        <Button
          size="sm" borderRadius="8px" px="14px"
          bg={showForm ? "gray.100" : GREEN} color={showForm ? "gray.600" : ORANGE}
          fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }}
          leftIcon={showForm ? <FiX size={13} /> : <FiPlus size={13} />}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancelar" : "Nova categoria"}
        </Button>
      </Flex>

      {/* Create form */}
      {showForm && (
        <Box
          bg="white" borderRadius="12px" p="16px 20px" mb="16px"
          border="1px solid rgba(0,74,51,.12)" boxShadow="0 2px 8px rgba(0,74,51,.06)"
        >
          <Text fontSize="12px" fontWeight="700" color="gray.500" mb="10px">Nova categoria</Text>
          <Input
            placeholder="Nome da categoria"
            value={categoryName}
            onChange={e => setCategoryName(e.target.value)}
            mb="10px"
            {...inputStyle}
          />
          <Flex gap="10px">
            <Select
              flex="1"
              value={subcategoryId}
              onChange={e => setSubcategoryId(e.target.value)}
              {...inputStyle}
            >
              <option value="">Selecione a subcategoria</option>
              {subcategories.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </Select>
            <Button
              borderRadius="8px" px="20px"
              bg={GREEN} color={ORANGE} fontWeight="700" fontSize="13px"
              _hover={{ opacity: 0.88 }}
              onClick={handleCreate}
            >
              Cadastrar
            </Button>
          </Flex>
        </Box>
      )}

      {/* List */}
      <Box bg="white" borderRadius="14px" border="1px solid rgba(0,74,51,.08)" overflow="hidden" boxShadow="0 2px 10px rgba(0,74,51,.04)">
        {categories.length === 0 ? (
          <Box py="32px" textAlign="center">
            <Text fontSize="14px" color="gray.400">Nenhuma categoria cadastrada</Text>
          </Box>
        ) : (
          categories.map((cat, i) => {
            const { subcategory, typeCount, typeNames, productCount } = relatedInfo(cat);
            return (
            <Box key={cat.id}>
              <Flex
                align="center" justify="space-between"
                px="20px" py="12px"
                _hover={{ bg: "rgba(0,74,51,.025)" }}
                transition="background 0.1s"
              >
                <Flex align="center" gap="10px" flex="1" minW="0">
                  <Text fontSize="11px" fontWeight="700" color="gray.300" minW="28px">#{cat.id}</Text>
                  <Box minW="0">
                    <Flex align="center" gap="8px" wrap="wrap">
                      <Text fontSize="14px" fontWeight="600" color="gray.700">{cat.name}</Text>
                      <Text
                        fontSize="10px" fontWeight="700" color="rgba(0,74,51,.55)" bg="rgba(0,74,51,.06)"
                        borderRadius="99px" px="8px" py="1px" textTransform="uppercase" letterSpacing="0.03em"
                      >
                        {subcategory?.name || "sem subcategoria"}
                      </Text>
                    </Flex>
                    <Text fontSize="11px" color="gray.400" mt="2px" noOfLines={1}>
                      {typeCount} tipo{typeCount !== 1 ? "s" : ""} de produto · {productCount} produto{productCount !== 1 ? "s" : ""}
                      {typeNames.length > 0 && ` — ${typeNames.join(", ")}`}
                    </Text>
                  </Box>
                </Flex>
                <EditCategory
                  categoryId={cat.id}
                  allSubcategories={subcategories}
                  onUpdateCategory={updateCategoryName}
                />
              </Flex>
              {i < categories.length - 1 && <Divider borderColor="rgba(0,74,51,.06)" />}
            </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
